import {
  generateTempClipId,
  isTempClipId,
} from '@src/services/WriteQueueService';
import { randomString } from '@src/utility/general';
import { LogManager } from '@src/utility/Logger';
import { z } from 'zod';

import type { VideoRepository } from './VideoRepository';
import type {
  LightweightVideoDTO,
  WriteQueueService,
} from '@src/services/WriteQueueService';
import type { VideoItem, VideoVersion } from '@src/types.ts/story';

const logger = LogManager.getLogger('repository:CachedVideoRepository');

const TEMP_ID_WAIT_TIMEOUT_MS = 30_000;
const SourcePlatformSchema = z.custom<VideoItem['source_platform']>(
  (value): value is VideoItem['source_platform'] =>
    value === 'creator-studio' || value === 'content-studio',
  { message: 'Invalid source platform' },
);
const AspectRatioSchema = z.custom<VideoItem['aspect_ratio']>(
  (value): value is VideoItem['aspect_ratio'] => typeof value === 'string',
  { message: 'Invalid aspect ratio' },
);
const VideoStatusSchema = z.custom<VideoItem['video_status']>(
  (value): value is VideoItem['video_status'] => value === 'editing',
  { message: 'Invalid video status' },
);
const CachedVideoItemSchema = z.object({
  id: z.string(),
  slug: z.string(),
  hash: z.string(),
  title: z.string(),
  aspect_ratio: AspectRatioSchema,
  source_platform: SourcePlatformSchema,
  clip_json: z.string().optional(),
  last_action_json: z.string().optional(),
  is_client_ready: z.boolean().optional(),
  video_json: z.string(),
  resources_metadata_json: z.string(),
  transcription_snapshot_json: z.string(),
  transcription_json: z.string(),
  extra_elements_json: z.string(),
  subtitles_json: z.string(),
  associated_videos: z.array(z.string()),
  ai_generated_content: z.array(
    z.object({
      id: z.string(),
      prompt: z.string(),
      generated_content: z.string(),
    }),
  ),
  thumbnail: z.object({
    upload_id: z.string(),
  }),
  video_status: VideoStatusSchema,
  shared_content: z.string(),
});

export interface CacheConfig {
  enabled: boolean;
}

type SaveVideoResult = { id: string; slug: string; hash: string };

type CachedVideoStatus = 'pending' | 'saving' | 'saved' | 'failed';

// Cached video entry - stores the full video data for temp IDs
interface CachedVideoEntry {
  id: string;
  slug: string;
  hash: string;
  video: LightweightVideoDTO;
  status: CachedVideoStatus;
  realId?: string; // Set once the background save completes
  realSlug?: string;
  realHash?: string;
  error?: string;
}

/**
 * CachedVideoRepository is a decorator that wraps VideoRepository to provide
 * transparent write-behind caching. It returns optimistic IDs immediately
 * and queues background saves through WriteQueueService.
 *
 * This enables non-blocking saves for Clip Producer and similar use cases
 * where immediate UI feedback is more important than waiting for Dato API.
 */
export class CachedVideoRepository {
  // Cache of pending/in-flight video data by temp ID
  private cache = new Map<string, CachedVideoEntry>();

  // Map from real ID back to temp ID (for lookups after save completes)
  private realIdToTempId = new Map<string, string>();

  constructor(
    private inner: VideoRepository,
    private writeQueueService: WriteQueueService,
    private config: CacheConfig,
  ) {}

  /**
   * Enable or disable the cache.
   * When disabled, all operations pass through to the inner repository.
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    logger.debug('CachedVideoRepository: cache enabled state changed', {
      enabled,
    });
  }

  /**
   * Check if caching is currently enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  // --- Cached method: saveLightweightVideo ---

  /**
   * Save a lightweight video with optimistic ID return.
   * When caching is enabled, returns immediately with a temp ID while
   * the actual save happens in the background.
   */
  async saveLightweightVideo(
    video: LightweightVideoDTO,
    storyId: string = '',
  ): Promise<SaveVideoResult> {
    if (!this.config.enabled) {
      return this.inner.saveLightweightVideo(video);
    }

    // Generate optimistic ID
    const tempId = generateTempClipId();
    const slug = `temp-${randomString(12)}`;
    const hash = `temp-${randomString(12)}`;

    // Store in cache immediately
    const entry: CachedVideoEntry = {
      id: tempId,
      slug,
      hash,
      video,
      status: 'pending',
    };
    this.cache.set(tempId, entry);

    logger.debug('CachedVideoRepository: created optimistic entry', {
      tempId,
      title: video.title,
    });

    // Queue background save with callbacks
    this.writeQueueService.enqueue([
      {
        id: tempId,
        video,
        storyId,
        onComplete: (realId, realSlug, realHash) => {
          // Update cache entry with real ID
          entry.realId = realId;
          entry.realSlug = realSlug;
          entry.realHash = realHash;
          entry.status = 'saved';
          this.realIdToTempId.set(realId, tempId);

          logger.debug('CachedVideoRepository: entry saved', {
            tempId,
            realId,
          });
        },
        onError: error => {
          entry.status = 'failed';
          entry.error = error.message;

          logger.error('CachedVideoRepository: entry failed to save', {
            tempId,
            error: error.message,
          });
        },
      },
    ]);

    // Return immediately with temp ID
    return { id: tempId, slug, hash };
  }

  // --- Read methods check cache first ---

  /**
   * Find a video by ID. Checks cache first for temp IDs.
   */
  async findVideo(id: string): Promise<VideoItem | null> {
    // Check cache first for temp IDs
    if (this.isOptimisticId(id)) {
      const entry = this.cache.get(id);
      if (entry) {
        logger.debug(
          'CachedVideoRepository: returning cached entry for temp ID',
          {
            tempId: id,
            status: entry.status,
          },
        );
        return this.cachedEntryToVideoItem(entry);
      }
    }

    // Check if this real ID maps to a cached entry
    const tempId = this.realIdToTempId.get(id);
    if (tempId) {
      const entry = this.cache.get(tempId);
      if (entry) {
        logger.debug(
          'CachedVideoRepository: returning cached entry for real ID',
          {
            realId: id,
            tempId,
            status: entry.status,
          },
        );
        return this.cachedEntryToVideoItem(entry);
      }
    }

    // Delegate to inner repository
    return this.inner.findVideo(id);
  }

  // --- Convert cached entry to VideoItem format ---

  private cachedEntryToVideoItem(entry: CachedVideoEntry): VideoItem {
    return CachedVideoItemSchema.parse({
      id: entry.realId ?? entry.id, // Use real ID if available
      slug: entry.realSlug ?? entry.slug,
      hash: entry.realHash ?? entry.hash,
      title: entry.video.title ?? '',
      aspect_ratio: entry.video.aspectRatio,
      source_platform: entry.video.sourcePlatform,
      clip_json: entry.video.clipJson
        ? JSON.stringify(entry.video.clipJson)
        : '{}',
      last_action_json: entry.video.lastActionJson
        ? JSON.stringify(entry.video.lastActionJson)
        : '{}',
      is_client_ready: entry.video.isClientReady,
      // Minimal required fields - these would be populated properly on full load
      video_json: '',
      resources_metadata_json: '{}',
      transcription_snapshot_json: '',
      transcription_json: JSON.stringify({ changelog: [] }),
      extra_elements_json: '{}',
      subtitles_json: '{}',
      associated_videos: [],
      ai_generated_content: [],
      thumbnail: { upload_id: '' },
      video_status: 'editing',
      shared_content: '',
    });
  }

  // --- Delegated methods (passthrough to inner repository) ---

  getVersionsByVideoId(
    id: string,
    pageLimit: number,
    page?: number,
    sizeAware?: boolean,
  ): Promise<{ videoVersions: VideoVersion[]; totalCount: number }> {
    return this.inner.getVersionsByVideoId(id, pageLimit, page, sizeAware);
  }

  async saveOrUpdateVideo(
    video: Parameters<VideoRepository['saveOrUpdateVideo']>[0],
  ): ReturnType<VideoRepository['saveOrUpdateVideo']> {
    // If the video has an optimistic/temp ID, resolve it before hitting DatoCMS
    if (video.id && this.isOptimisticId(video.id)) {
      return this.resolveAndSave(video);
    }

    return this.inner.saveOrUpdateVideo(video);
  }

  /**
   * Resolve a temp ID by waiting for the background save, then delegate
   * to the inner repository with the real ID. Falls back to CREATE if
   * the background save fails or times out.
   */
  private async resolveAndSave(
    video: Parameters<VideoRepository['saveOrUpdateVideo']>[0],
  ): ReturnType<VideoRepository['saveOrUpdateVideo']> {
    const tempId = video.id!;
    const resolved = await this.resolveTemporaryId(tempId);

    if (resolved.ok) {
      return this.inner.saveOrUpdateVideo({ ...video, id: resolved.realId });
    }

    // Background save failed — fall back to CREATE by stripping the ID
    logger.warn(
      'saveOrUpdateVideo: background save failed, falling back to create',
      { tempId, error: resolved.error },
    );
    return this.inner.saveOrUpdateVideo({ ...video, id: undefined });
  }

  async updateVideo(
    ...args: Parameters<VideoRepository['updateVideo']>
  ): ReturnType<VideoRepository['updateVideo']> {
    const [video] = args;
    if (this.isOptimisticId(video.id)) {
      // A title-only update is a rename. It patches the queued create in
      // place instead of waiting for the temp ID: a rename while the clip is
      // still saving used to wait out TEMP_ID_WAIT_TIMEOUT_MS and throw, and
      // the record was then born with the old name (PLAT-11777).
      const patched = this.patchQueuedTitle(video);
      if (patched) return patched;

      const resolved = await this.resolveTemporaryId(video.id);
      if (!resolved.ok) {
        throw new Error(
          `Cannot update video with unresolved temp ID ${video.id}: ${resolved.error}`,
        );
      }
      return this.inner.updateVideo({ ...video, id: resolved.realId });
    }
    return this.inner.updateVideo(...args);
  }

  /**
   * Route a title-only update for a temp ID through the write queue.
   * Returns the result to hand back, or null when the update carries other
   * fields or the queue no longer holds the item, so the caller falls back
   * to waiting for the real ID.
   */
  private patchQueuedTitle(
    video: Parameters<VideoRepository['updateVideo']>[0],
  ): Promise<SaveVideoResult> | null {
    const isTitleOnly = Object.keys(video).every(
      key => key === 'id' || key === 'title',
    );
    if (!isTitleOnly || !video.title) return null;

    const patched = this.writeQueueService.patchQueuedVideoTitle(
      video.id,
      video.title,
    );
    switch (patched.status) {
      case 'queued': {
        const entry = this.cache.get(video.id);
        return Promise.resolve({
          id: video.id,
          slug: entry?.slug ?? '',
          hash: entry?.hash ?? '',
        });
      }
      case 'saved':
        return this.inner.updateVideo({ ...video, id: patched.realId });
      default:
        return null;
    }
  }

  /**
   * Wait for a temp ID to be resolved to a real DatoCMS ID via the
   * background write queue. Returns the real ID on success, or an
   * error string on timeout/failure.
   */
  private async resolveTemporaryId(
    tempId: string,
  ): Promise<{ ok: true; realId: string } | { ok: false; error: string }> {
    const timeoutPromise = new Promise<{ error: string }>(resolve => {
      setTimeout(
        () => resolve({ error: `Timed out waiting for clip ${tempId}` }),
        TEMP_ID_WAIT_TIMEOUT_MS,
      );
    });

    const result = await Promise.race([
      this.writeQueueService.waitForItem(tempId),
      timeoutPromise,
    ]);

    if ('realId' in result) {
      logger.info('resolveTemporaryId: resolved', {
        tempId,
        realId: result.realId,
      });
      return { ok: true, realId: result.realId };
    }

    return { ok: false, error: result.error };
  }

  createVideo(
    ...args: Parameters<VideoRepository['createVideo']>
  ): ReturnType<VideoRepository['createVideo']> {
    return this.inner.createVideo(...args);
  }

  copyParentAndAssociatedVideos(
    ...args: Parameters<VideoRepository['copyParentAndAssociatedVideos']>
  ): ReturnType<VideoRepository['copyParentAndAssociatedVideos']> {
    return this.inner.copyParentAndAssociatedVideos(...args);
  }

  updateThumbnail(
    ...args: Parameters<VideoRepository['updateThumbnail']>
  ): ReturnType<VideoRepository['updateThumbnail']> {
    return this.inner.updateThumbnail(...args);
  }

  estimateRecordSize(
    ...args: Parameters<VideoRepository['estimateRecordSize']>
  ): ReturnType<VideoRepository['estimateRecordSize']> {
    return this.inner.estimateRecordSize(...args);
  }

  // --- Cache utility methods ---

  /**
   * Check if an ID is an optimistic/temp ID
   */
  isOptimisticId(id: string): boolean {
    return isTempClipId(id);
  }

  /**
   * Get the cache status for a temp ID
   */
  getCacheStatus(id: string): CachedVideoStatus | null {
    const entry = this.cache.get(id);
    return entry?.status ?? null;
  }

  /**
   * Get the real ID for a temp ID (if save has completed)
   */
  getRealId(tempId: string): string | undefined {
    return this.cache.get(tempId)?.realId;
  }

  /**
   * Check if a temp ID exists in the cache
   */
  hasCachedEntry(id: string): boolean {
    return this.cache.has(id) || this.realIdToTempId.has(id);
  }

  /**
   * Get cache statistics for debugging/monitoring
   */
  getCacheStats(): {
    totalEntries: number;
    pending: number;
    saving: number;
    saved: number;
    failed: number;
  } {
    let pending = 0;
    let saving = 0;
    let saved = 0;
    let failed = 0;

    for (const entry of this.cache.values()) {
      switch (entry.status) {
        case 'pending':
          pending++;
          break;
        case 'saving':
          saving++;
          break;
        case 'saved':
          saved++;
          break;
        case 'failed':
          failed++;
          break;
      }
    }

    return {
      totalEntries: this.cache.size,
      pending,
      saving,
      saved,
      failed,
    };
  }

  /**
   * Clear saved entries from the cache (for memory cleanup)
   */
  clearSavedEntries(): void {
    const idsToDelete: string[] = [];
    for (const [id, entry] of this.cache.entries()) {
      if (entry.status === 'saved') {
        idsToDelete.push(id);
        if (entry.realId) {
          this.realIdToTempId.delete(entry.realId);
        }
      }
    }
    for (const id of idsToDelete) {
      this.cache.delete(id);
    }

    logger.debug('CachedVideoRepository: cleared saved entries', {
      count: idsToDelete.length,
    });
  }
}
